import type { Flavor, NodeRoom } from '@/api/types'
import { sizeMb } from '@/ui/format'

/** 노드 하나가 받아야 하는 것 — VM 생성 페이지에서 지금 고른 사양. 0 은 아직 정해지지 않은 축이라 재지 않는다. */
export type RoomNeed = { cores: number; memoryMb: number; gpuModel: string; gpus: number }

/**
 * 그 노드가 지금 고른 사양을 받지 못하는 까닭, 받으면 null. 노드를 고르는 사람(F-126)의 노드 목록이 잠긴 노드에 다는 사유다.
 * 남음을 모르는 노드는 배치도 쓰지 않으므로 잠긴다. 디스크는 재지 않는다 — 어느 저장소 단계에 놓일지와 안전 여유는 배치가 정하고,
 * 맞지 않으면 신청이 409 로 말한다. 남음은 본 사람에게 잡혀 있는 것이 아니라 판정은 서버의 것이다.
 */
export function roomShort(r: NodeRoom, need: RoomNeed): string | null {
  if (r.cores.available === null || r.memory_mb.available === null) return '자원을 알 수 없음'
  if (need.cores > r.cores.available) return '코어 부족'
  if (need.memoryMb > r.memory_mb.available) return '메모리 부족'
  if (need.gpus > 0) {
    const g = r.gpus.find((x) => x.model === need.gpuModel)
    if (!g || g.free < need.gpus) return 'GPU 부족'
  }
  return null
}

/** 그 노드에 남은 코어·메모리로 받을 수 없는 유형인가. 자유로 둔 축은 신청이 적는 값이라 여기서 재지 않는다. */
export function flavorOverRoom(f: Flavor, r: NodeRoom): boolean {
  const cores = r.cores.available
  const memory = r.memory_mb.available
  return (f.cores != null && cores !== null && f.cores > cores) || (f.memory_mb != null && memory !== null && f.memory_mb > memory)
}

/** 남음을 아는 노드 — 배치가 쓰는 노드다. */
function known(rooms: NodeRoom[]): NodeRoom[] {
  return rooms.filter((r) => r.cores.available !== null && r.memory_mb.available !== null)
}

/**
 * 노드를 고르지 않은 신청(자동 배치)이 이 사양을 받을 노드를 하나라도 갖는가(B-47). 잴 노드가 없으면 참이다 —
 * 그때의 판정은 서버의 것이고, 모든 것을 잠그면 무엇을 고쳐야 하는지 말할 자리가 없다.
 */
export function fitsSomewhere(rooms: NodeRoom[], need: RoomNeed): boolean {
  const list = known(rooms)
  return list.length === 0 || list.some((r) => !roomShort(r, need))
}

/** 한 노드에 남은 그 모델의 카드 중 가장 많은 수 — 자동 배치에서 고를 수 있는 장 수의 끝이다. 한 VM 은 한 노드에 놓인다. */
export function mostCardsOnANode(rooms: NodeRoom[], model: string): number {
  return Math.max(0, ...rooms.map((r) => r.gpus.find((g) => g.model === model)?.free ?? 0))
}

/** 자동 배치의 막힘이 서는 칸과 그 문장. */
export type PlacementGap = { field: 'gpu' | 'cores' | 'memory' | 'flavor'; text: string }

/**
 * 자동 배치에서 고른 사양을 한꺼번에 받을 노드가 없을 때 무엇을 줄이면 되는지(B-47). 받을 노드가 있거나 잴 노드가 없으면 null.
 * 노드마다의 판정은 `roomShort` 와 같은 산수이고, 카드부터 본다 — 카드가 남은 노드 안에서 코어를, 그 코어도 받는 노드 안에서 메모리를 잰다.
 * `fixed` 는 VM 유형이 정한 축이다. 그 축은 칸이 아니라 유형을 바꿔야 줄어들어 막힘이 유형 칸에 선다.
 */
export function placementGap(rooms: NodeRoom[], need: RoomNeed, fixed: { cores: boolean; memory: boolean }): PlacementGap | null {
  if (fitsSomewhere(rooms, need)) return null
  let pool = known(rooms)
  let where = '한 노드에 남은'
  if (need.gpus > 0) {
    const most = mostCardsOnANode(pool, need.gpuModel)
    if (most < need.gpus) {
      const text = most === 0 ? `남은 ${need.gpuModel} 이 없습니다. 다른 GPU 를 고르세요.` : `한 노드에 남은 ${need.gpuModel} 은 최대 ${most}장입니다. 장 수를 줄이세요.`
      return { field: 'gpu', text }
    }
    pool = pool.filter((r) => (r.gpus.find((g) => g.model === need.gpuModel)?.free ?? 0) >= need.gpus)
    where = `${need.gpuModel} ${need.gpus}장이 남은 노드의`
  }
  const cores = Math.max(0, ...pool.map((r) => r.cores.available ?? 0))
  if (need.cores > cores) {
    const act = fixed.cores ? '코어가 적은 VM 유형을 고르세요.' : '코어를 줄이세요.'
    return { field: fixed.cores ? 'flavor' : 'cores', text: `${where} 코어는 최대 ${cores}코어입니다. ${act}` }
  }
  pool = pool.filter((r) => (r.cores.available ?? 0) >= need.cores)
  const memory = Math.max(0, ...pool.map((r) => r.memory_mb.available ?? 0))
  const withCores = need.gpus > 0 ? `${need.gpuModel} ${need.gpus}장과 ${need.cores}코어가 남은 노드의` : `${need.cores}코어가 남은 노드의`
  const act = fixed.memory ? '메모리가 적은 VM 유형을 고르세요.' : '메모리를 줄이세요.'
  return { field: fixed.memory ? 'flavor' : 'memory', text: `${withCores} 메모리는 최대 ${sizeMb(memory)} 입니다. ${act}` }
}
